import React, {useMemo, useState} from "react";
import _ from "lodash";
import {Link, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useAtom, useAtomValue, useSetAtom} from "jotai";
import {
    AppBar,
    Avatar,
    Box,
    Button,
    Divider,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Tab,
    Tabs,
    Toolbar,
    Typography,
} from "@mui/material";
import DownIcon from "@mui/icons-material/KeyboardArrowDown";
import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import ApiIcon from "@mui/icons-material/CodeOutlined";
import LogoutIcon from "@mui/icons-material/LogoutOutlined";
import {authenticatedUserLoadable, authenticatedUserNotificationsStatsLoadable} from "../store/authenticatedUserStore";
import {impersonationAtom} from "../store/impersonationStore";
import {getUserFullName} from "../utils/userUtils";
import logo from "../assets/images/irriwatch-logo.svg";
import {authenticationAtom} from "../store/authenticationStore";
import {NotificationsDialog} from "./dialogs/NotificationsDialog";
import {LicenseDialog} from "./dialogs/LicenseDialog";
import {ContactSupportDialog} from "./dialogs/ContactSupportDialog";
import {ContactSupportConfirmDialog} from "./dialogs/ContactSupportConfirmDialog";
import {DocumentationDialog} from "./dialogs/DocumentationDialog";
import {UserEditDialog} from "./dialogs/UserEditDialog";
import {UserPasswordChangeDialog} from "./dialogs/UserPasswordChangeDialog";
import {UserApiCredentialsDialog} from "./dialogs/UserApiCredentialsDialog";
import {routerCompanyIdAtom, routerFarmIdAtom, routerFieldIdAtom} from "../store/routerStore";
import {useAccessMarker} from "../hooks/accessMarkerHooks";
import {ALL_ACCESS_FUNCTIONS} from "../utils/accessControlUtils";
import {CompanyIcon} from "./icons/CompanyIcon";
import {MapIcon} from "./icons/MapIcon";
import {InsightsIcon} from "./icons/InsightsIcon";
import {AdminIcon} from "./icons/AdminIcon";
import {UserIcon} from "./icons/UserIcon";
import {NotificationsIcon} from "./icons/NotificationsIcon";
import {PasswordIcon} from "./icons/PasswordIcon";
import {SupportIcon} from "./icons/SupportIcon";
import {useCompanyBrandingQuery} from "../hooks/companyBrandingHooks";
import {useUnSeenNotifications} from "../hooks/notificationsHook";
import {useLogout} from "../hooks/useLogout.ts";
import {swallowException} from "../utils/appUtils.ts";

interface MenuItem {
    label: string;
    href: string;
    icon: React.ReactElement;
}

export const Header: React.FC = () => {
    const {t} = useTranslation();
    const companyId = useAtomValue(routerCompanyIdAtom);
    const {data: logoUrl} = useCompanyBrandingQuery(companyId);
    const farmId = useAtomValue(routerFarmIdAtom);
    const fieldId = useAtomValue(routerFieldIdAtom);
    const location = useLocation();
    const pathPrefix = location.pathname?.split("/", 2).join("/");
    const selectedTabValue = HEADER_MENU_VIABLE_PATHS.includes(pathPrefix) ? pathPrefix : false;
    const authenticatedUserState = useAtomValue(authenticatedUserLoadable);
    const notificationsStatsState = useAtomValue(authenticatedUserNotificationsStatsLoadable);
    const userFullName = authenticatedUserState.state === "hasData" ? getUserFullName(authenticatedUserState.data) : "";
    const access = useAccessMarker(ACCESS_FUNCTIONS);
    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const [impersonation, setImpersonation] = useAtom(impersonationAtom);
    const isUserInitialized = authenticatedUserState.state === "hasData";
    const refreshAuth = useSetAtom(authenticationAtom);
    const [notificationsOpened, setNotificationsOpened] = useState<boolean>(false);
    const [userSettingsOpened, setUserSettingsOpened] = useState<boolean>(false);
    const [passwordChangeOpened, setPasswordChangeOpened] = useState<boolean>(false);
    const [apiCredentialsOpened, setApiCredentialsOpened] = useState<boolean>(false);
    const [documentationOpened, setDocumentationOpened] = useState<boolean>(false);
    const [contactOpened, setContactOpened] = useState<boolean>(false);
    const [contactConfirmOpened, setContactConfirmOpened] = useState<boolean>(false);
    const [licenseOpened, setLicenseOpened] = useState<boolean>(false);
    const unSeenNotifications = useUnSeenNotifications();
    const {logout} = useLogout();

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleExitImpersonationMenu = () => {
        handleCloseUserMenu();
        setImpersonation(undefined);
    };

    const handleLogoutMenu = () => {
        handleCloseUserMenu();
        logout().catch(swallowException);
    };

    const supportContactClose = (isSuccessful: boolean) => {
        setContactOpened(false);
        if (isSuccessful) {
            setContactConfirmOpened(true);
        }
    };

    const notificationsNumber = useMemo(() => {
        return notificationsStatsState.state === "hasData"
            ? notificationsStatsState.data.pendingInvitations +
                  notificationsStatsState.data.pendingOrders +
                  (unSeenNotifications?.length ? unSeenNotifications.length : 0)
            : 0;
    }, [unSeenNotifications, notificationsStatsState]);

    return (
        <>
            <AppBar
                color={getThemeColor(impersonation)}
                position="static"
                elevation={0}
                data-test="appHeader"
                sx={{height: "3.5rem"}}
            >
                <Toolbar
                    disableGutters
                    variant="dense"
                    sx={{
                        minHeight: "3.5rem",
                        height: "3.5rem",
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="start"
                        sx={{
                            width: "17.75rem",
                            minWidth: "17.75rem",
                            paddingX: "1.125rem",
                            paddingY: "0.5rem",
                            height: "2.5rem",
                        }}
                    >
                        <img
                            src={logoUrl ?? logo}
                            alt="Logo"
                            width="104px"
                            height="100%"
                            style={{objectFit: "contain"}}
                        />
                        <Stack direction="row" alignItems="end" sx={{height: "100%"}}>
                            <Typography fontSize={12} lineHeight="0.75em">
                                powered by Hydrosat
                            </Typography>
                        </Stack>
                    </Stack>

                    <div>
                        {isUserInitialized && (
                            <Tabs
                                value={selectedTabValue}
                                textColor="inherit"
                                variant="fullWidth"
                                TabIndicatorProps={{
                                    sx: {
                                        height: "0",
                                    },
                                }}
                                sx={{
                                    minHeight: "auto",
                                    "& a:hover": {backgroundColor: getHexaColor(impersonation, "hover")},
                                    "& .Mui-selected": {backgroundColor: getHexaColor(impersonation, "selected")},
                                }}
                            >
                                {renderMenuItem({
                                    value: "/insights",
                                    href: getMainPagePath({basePath: "/insights", companyId, farmId, fieldId}),
                                    label: t("header.menu.insights"),
                                    icon: (
                                        <InsightsIcon
                                            sx={{
                                                stroke: impersonation ? "#ecaaf7" : "#31F9F1",
                                                marginRight: "0.625rem",
                                                width: "1.125rem",
                                                height: "1.125rem",
                                            }}
                                        />
                                    ),
                                    dataTest: "dashboardMenu",
                                })}
                                {renderMenuItem({
                                    value: "/map",
                                    href: getMainPagePath({basePath: "/map", companyId, farmId, fieldId}),
                                    label: t("header.menu.map"),
                                    icon: (
                                        <MapIcon
                                            sx={{
                                                stroke: impersonation ? "#ecaaf7" : "#31F9F1",
                                                marginRight: "0.625rem",
                                                width: "1.125rem",
                                                height: "1.125rem",
                                            }}
                                        />
                                    ),
                                    dataTest: "mapMenu",
                                })}
                                {renderMenuItem({
                                    value: "/organization",
                                    href: companyId ? `/organization/${companyId}` : "/organization",
                                    label: t("header.menu.organization"),
                                    icon: (
                                        <CompanyIcon
                                            sx={{
                                                stroke: impersonation ? "#ecaaf7" : "#31F9F1",
                                                marginRight: "0.625rem",
                                                width: "1.125rem",
                                                height: "1.125rem",
                                            }}
                                        />
                                    ),
                                    dataTest: "organizationMenu",
                                })}
                                {access.canViewAdminPage &&
                                    renderMenuItem({
                                        value: "/admin",
                                        href: "/admin",
                                        label: t("header.menu.admin"),
                                        icon: (
                                            <AdminIcon
                                                sx={{
                                                    stroke: impersonation ? "#ecaaf7" : "#31F9F1",
                                                    marginRight: "0.625rem",
                                                    width: "1.25rem",
                                                    height: "1.25rem",
                                                }}
                                            />
                                        ),
                                        dataTest: "adminMenu",
                                    })}
                            </Tabs>
                        )}
                    </div>
                    <Box sx={{flexGrow: 1, flexShrink: 1, flexBasis: "0%"}} />

                    <div>
                        <Button
                            aria-label="menu"
                            variant="contained"
                            disableElevation={true}
                            onClick={handleOpenUserMenu}
                            color={getThemeColor(impersonation)}
                            data-test="userMenuButton"
                            sx={{
                                height: "3.5rem",
                                p: 0,
                                borderRadius: 0,
                                borderColor: "primary.main",
                                "& .MuiButton-startIcon": {margin: 0},
                                "&:hover": {backgroundColor: getHexaColor(impersonation, "hover")},
                            }}
                        >
                            <Stack paddingX="1.25rem" direction="row" gap="0.5rem" alignItems="center">
                                <UserIcon
                                    sx={{
                                        stroke: impersonation ? "#ecaaf7" : "#31F9F1",
                                        width: "1.125rem",
                                        height: "1.125rem",
                                        marginRight: "0.2rem",
                                    }}
                                />
                                <Typography
                                    fontSize={14}
                                    textTransform="none"
                                    fontWeight={500}
                                    noWrap
                                    textOverflow="ellipsis"
                                    data-test="authUserName"
                                >
                                    {userFullName}
                                </Typography>
                                {notificationsNumber > 0 && (
                                    <div>
                                        <Avatar sx={{bgcolor: "#EA463C", width: 24, height: 24, fontSize: 14}}>
                                            {notificationsNumber}
                                        </Avatar>
                                    </div>
                                )}
                                <DownIcon sx={{color: "#31F9F1", width: "1rem", height: "1rem"}} />
                            </Stack>
                        </Button>
                        <Menu
                            sx={{mt: "2.5rem"}}
                            slotProps={{paper: {style: {minWidth: "14rem"}}}}
                            id="menu-appbar"
                            anchorEl={anchorElUser}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            open={Boolean(anchorElUser)}
                            onClose={handleCloseUserMenu}
                        >
                            {renderUserMenuItem({
                                label: t("header.settings.menu.notifications"),
                                icon: (
                                    <NotificationsIcon
                                        sx={{
                                            stroke: "#8F94A8",
                                            width: "1.125rem",
                                            height: "1.125rem",
                                        }}
                                    />
                                ),
                                badge: notificationsNumber > 0 ? notificationsNumber : undefined,
                                dataTest: "notificationsMenu",
                                onClick: () => {
                                    handleCloseUserMenu();
                                    setNotificationsOpened(true);
                                },
                            })}
                            {renderUserMenuItem({
                                label: t("header.settings.menu.user_settings"),
                                icon: (
                                    <UserIcon
                                        sx={{
                                            stroke: "#8F94A8",
                                            width: "1.125rem",
                                            height: "1.125rem",
                                        }}
                                    />
                                ),
                                dataTest: "userSettingsMenu",
                                onClick: () => {
                                    handleCloseUserMenu();
                                    setUserSettingsOpened(true);
                                },
                            })}
                            {renderUserMenuItem({
                                label: t("header.settings.menu.change_password"),
                                icon: (
                                    <PasswordIcon
                                        sx={{
                                            stroke: "#8F94A8",
                                            width: "1.125rem",
                                            height: "1.125rem",
                                        }}
                                    />
                                ),
                                dataTest: "changePasswordMenu",
                                onClick: () => {
                                    handleCloseUserMenu();
                                    setPasswordChangeOpened(true);
                                },
                            })}
                            {renderUserMenuItem({
                                label: t("header.settings.menu.api_credentials"),
                                icon: <ApiIcon fontSize="small" sx={{color: "#8F94A8"}} />,
                                dataTest: "apiCredentialsMenu",
                                onClick: () => {
                                    handleCloseUserMenu();
                                    setApiCredentialsOpened(true);
                                },
                            })}
                            {renderUserMenuItem({
                                label: t("header.settings.menu.documentation"),
                                icon: <HelpIcon fontSize="small" sx={{color: "#8F94A8"}} />,
                                dataTest: "documentationMenu",
                                onClick: () => {
                                    handleCloseUserMenu();
                                    setDocumentationOpened(true);
                                },
                            })}
                            {renderUserMenuItem({
                                label: t("header.settings.menu.contact"),
                                icon: (
                                    <SupportIcon
                                        sx={{
                                            stroke: "#8F94A8",
                                            width: "1.125rem",
                                            height: "1.125rem",
                                        }}
                                    />
                                ),
                                dataTest: "contactMenu",
                                onClick: () => {
                                    handleCloseUserMenu();
                                    setContactOpened(true);
                                },
                            })}
                            {renderUserMenuItem({
                                label: t("header.settings.menu.licence"),
                                icon: <InfoIcon fontSize="small" sx={{color: "#8F94A8"}} />,
                                dataTest: "licenseMenu",
                                onClick: () => {
                                    handleCloseUserMenu();
                                    setLicenseOpened(true);
                                },
                            })}
                            <Divider light />
                            {impersonation !== undefined &&
                                renderUserMenuItem({
                                    label: t("header.settings.menu.exit_impersonation"),
                                    icon: <LogoutIcon fontSize="small" sx={{color: "#8F94A8"}} />,
                                    dataTest: "impersonationMenu",
                                    onClick: handleExitImpersonationMenu,
                                })}
                            {renderUserMenuItem({
                                label: t("header.settings.menu.logout"),
                                icon: <LogoutIcon fontSize="small" />,
                                dataTest: "logoutMenu",
                                onClick: handleLogoutMenu,
                            })}
                        </Menu>
                    </div>
                </Toolbar>
            </AppBar>
            {notificationsOpened && (
                <NotificationsDialog open={notificationsOpened} onClose={() => setNotificationsOpened(false)} />
            )}
            {userSettingsOpened && isUserInitialized && (
                <UserEditDialog
                    mode="settings"
                    user={authenticatedUserState.data}
                    open={userSettingsOpened}
                    onClose={(isSuccessful: boolean) => {
                        setUserSettingsOpened(false);
                        if (isSuccessful) {
                            refreshAuth();
                        }
                    }}
                />
            )}
            {passwordChangeOpened && (
                <UserPasswordChangeDialog open={passwordChangeOpened} onClose={() => setPasswordChangeOpened(false)} />
            )}
            {apiCredentialsOpened && isUserInitialized && (
                <UserApiCredentialsDialog
                    open={apiCredentialsOpened}
                    userId={authenticatedUserState.data.id}
                    onClose={() => setApiCredentialsOpened(false)}
                />
            )}
            {documentationOpened && (
                <DocumentationDialog open={documentationOpened} onClose={() => setDocumentationOpened(false)} />
            )}
            {contactOpened && <ContactSupportDialog open={contactOpened} onClose={supportContactClose} />}
            {contactConfirmOpened && (
                <ContactSupportConfirmDialog
                    open={contactConfirmOpened}
                    onClose={() => setContactConfirmOpened(false)}
                />
            )}
            {licenseOpened && <LicenseDialog open={licenseOpened} onClose={() => setLicenseOpened(false)} />}
        </>
    );
};

const renderMenuItem = ({href, icon, label, dataTest, value}: MenuItem & {value: string; dataTest: string}) => (
    <Tab
        key={href}
        label={label}
        icon={icon}
        iconPosition="start"
        value={value}
        id={href}
        component={Link}
        to={{
            pathname: href,
            search: location.search,
        }}
        sx={{
            minHeight: "3.5rem",
            height: "3.5rem",
            minWidth: "auto",
            flex: "auto 0 0",
            letterSpacing: ".04em",
            whiteSpace: "nowrap",
            fontSize: "1rem",
            opacity: 1,
            px: "1.25rem",
        }}
        data-test={dataTest}
    />
);

interface UserMenuItem {
    label: string;
    icon: React.ReactElement;
    badge?: string | number;
    dataTest: string;
    onClick?: () => void;
}

const renderUserMenuItem = ({icon, label, onClick, badge, dataTest}: UserMenuItem) => (
    <MenuItem onClick={onClick} sx={{paddingY: "0.6rem"}} data-test={dataTest}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText>{label}</ListItemText>
        {badge && (
            <Avatar
                variant="rounded"
                sx={{
                    bgcolor: "#FFE4E0",
                    color: "#A91313",
                    width: 24,
                    height: 24,
                    fontSize: 14,
                }}
            >
                {badge}
            </Avatar>
        )}
    </MenuItem>
);

const getMainPagePath = (props: {
    basePath: string;
    companyId: string | undefined;
    farmId: string | undefined;
    fieldId: string | undefined;
}) => {
    const {basePath, companyId, farmId, fieldId} = props;
    if (companyId) {
        if (farmId) {
            return `${basePath}/${companyId}/farm/${farmId}`;
        } else if (fieldId) {
            return `${basePath}/${companyId}/field/${fieldId}`;
        } else {
            return `${basePath}/${companyId}`;
        }
    } else {
        return basePath;
    }
};

const getThemeColor = (impersonation: string | undefined) => {
    const env = import.meta.env.MODE?.toLowerCase();

    if (env === "development" || env === "sandbox" || env === "staging") return env;

    return impersonation ? "impersonation" : "dark";
};

const getHexaColor = (impersonation: string | undefined, type: "hover" | "selected") => {
    const env = import.meta.env.MODE?.toLowerCase();

    if (env === "development") return "#3B8323";
    if (env === "sandbox") return "#D79402";
    if (env === "staging") return "#DB0000";

    if (type === "hover") return impersonation ? "#b62dcd" : "#505cc2";
    if (type === "selected") return impersonation ? "#a92abe" : "#404DB8";

    return "#171e39";
};

const HEADER_MENU_VIABLE_PATHS = ["/insights", "/map", "/organization", "/admin"];

const ACCESS_FUNCTIONS = _.pick(ALL_ACCESS_FUNCTIONS, ["canViewAdminPage"]);
